import React, { memo, useState, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Collapse from "@mui/material/Collapse";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslation } from "react-i18next";
import color from "../../Constants/colors";
import QuestionItem from "./QuestionItem";
import { Features, UserRole } from "../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { useDebouncedCallback } from "../../Hooks/useDebouncedCallback";
import { ComoInput } from "../Common/ComoInput";
import { ComoButton } from "../Common/ComoButton";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

interface CategoryQuestion {
  id: string;
  text: string;
  altText?: string;
  order: number;
}

interface Category {
  id: string;
  name: string;
  subtitle?: string;
  description?: string;
  questions: CategoryQuestion[];
  extraTable?: boolean;
  extraTableProperties?: string[];
  questionCategoryKey?: string;
  order?: number;
}

interface CategoryItemProps {
  category: Category;
  newQuestionText: string;
  onRemoveCategory: (id: string) => void;
  onUpdateCategory: (id: string, updates: Partial<Category>) => void;
  onAddQuestion: (categoryId: string, questionText: string) => void;
  onRemoveQuestion: (categoryId: string, questionId: string) => void;
  onUpdateQuestion: (
    categoryId: string,
    questionId: string,
    newText: string,
  ) => void;
  onUpdateQuestionAltText: (
    categoryId: string,
    questionId: string,
    altText: string,
  ) => void;
  onUpdateNewQuestionText: (categoryId: string, text: string) => void;
  onExtraTableFlag: (
    categoryId: string,
    extraTable: boolean,
    extraTableProperties?: string[],
  ) => void;
  onReorderQuestions: (
    categoryId: string,
    questions: CategoryQuestion[],
  ) => void;
  isDirty?: boolean;
  dirtyQuestionIds?: Set<string>;
  action?: "create" | "edit";
}

interface SortableQuestionItemProps {
  question: CategoryQuestion;
  isDirty?: boolean;
  onRemove: () => void;
  onEdit: (newText: string) => void;
  onAltTextEdit: (altText: string) => void;
}

const SortableQuestionItem: React.FC<SortableQuestionItemProps> = ({
  question,
  isDirty,
  onRemove,
  onEdit,
  onAltTextEdit,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: question.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    position: "relative" as const,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <QuestionItem
          question={{
            id: question.id,
            text: question.text,
            altText: question.altText,
          }}
          isDirty={isDirty}
          onRemove={onRemove}
          onEdit={onEdit}
          onAltTextEdit={onAltTextEdit}
        />
      </Box>
    </Box>
  );
};

const CategoryItem: React.FC<CategoryItemProps> = memo(
  ({
    category,
    newQuestionText,
    onRemoveCategory,
    onUpdateCategory,
    onAddQuestion,
    onRemoveQuestion,
    onUpdateQuestion,
    onUpdateQuestionAltText,
    onUpdateNewQuestionText,
    onExtraTableFlag,
    onReorderQuestions,
    isDirty,
    dirtyQuestionIds,
    action,
  }) => {
    const { t } = useTranslation("createQuestionList");
    const { colors: workspace } = useWorkspaceColors();

    const [localName, setLocalName] = useState(category.name);
    const [localSubtitle, setLocalSubtitle] = useState(category.subtitle || "");
    const [localDescription, setLocalDescription] = useState(
      category.description || "",
    );
    const [localNewQuestion, setLocalNewQuestion] = useState(
      newQuestionText || "",
    );
    const [localExtraTableProps, setLocalExtraTableProps] = useState(
      category.extraTableProperties || [""],
    );
    const [expanded, setExpanded] = useState(category.questions.length === 0);

    const toggleCategory = () => {
      setExpanded((prev) => !prev);
    };

    const debouncedUpdateExtraTable = useDebouncedCallback(
      (id: string, extraTable: boolean, props: string[] | undefined) => {
        onExtraTableFlag(id, extraTable, props);
      },
      500,
    );

    const handleExtraTablePropChange = useCallback(
      (index: number, value: string) => {
        const newProps = [...localExtraTableProps];
        newProps[index] = value;
        setLocalExtraTableProps(newProps);

        debouncedUpdateExtraTable(
          category.id,
          category.extraTable || false,
          newProps,
        );
      },
      [
        localExtraTableProps,
        category.id,
        category.extraTable,
        debouncedUpdateExtraTable,
      ],
    );

    const addExtraTableProperty = useCallback(() => {
      const newProps = [...localExtraTableProps, ""];
      setLocalExtraTableProps(newProps);

      debouncedUpdateExtraTable(
        category.id,
        category.extraTable || false,
        newProps,
      );
    }, [
      localExtraTableProps,
      category.id,
      category.extraTable,
      debouncedUpdateExtraTable,
    ]);

    const removeExtraTableProperty = useCallback(
      (index: number) => {
        setLocalExtraTableProps((prev) => {
          const newProps = prev.filter((_, i) => i !== index);

          debouncedUpdateExtraTable(
            category.id,
            category.extraTable || false,
            newProps,
          );

          return newProps;
        });
      },
      [category.id, category.extraTable, debouncedUpdateExtraTable],
    );

    const debouncedUpdateName = useDebouncedCallback(
      (id: string, value: string) => {
        onUpdateCategory(id, {
          name: value,
        });
      },
      500,
    );

    const debouncedUpdateSubtitle = useDebouncedCallback(
      (id: string, value: string) => {
        onUpdateCategory(id, {
          subtitle: value,
        });
      },
      500,
    );

    const debouncedUpdateDescription = useDebouncedCallback(
      (id: string, value: string) => {
        onUpdateCategory(id, {
          description: value,
        });
      },
      500,
    );

    const handleNameChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalName(value);
        debouncedUpdateName(category.id, value);
      },
      [category.id, debouncedUpdateName],
    );

    const handleSubtitleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalSubtitle(value);
        debouncedUpdateSubtitle(category.id, value);
      },
      [category.id, debouncedUpdateSubtitle],
    );

    const handleDescriptionChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalDescription(value);
        debouncedUpdateDescription(category.id, value);
      },
      [category.id, debouncedUpdateDescription],
    );

    const handleNewQuestionChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalNewQuestion(value);
      },
      [],
    );

    const handleAddQuestion = useCallback(() => {
      if (localNewQuestion.trim()) {
        const questionText = localNewQuestion.trim();

        onAddQuestion(category.id, questionText);
        setLocalNewQuestion("");
        onUpdateNewQuestionText(category.id, "");
      }
    }, [localNewQuestion, category.id, onAddQuestion, onUpdateNewQuestionText]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
          handleAddQuestion();
        }
      },
      [handleAddQuestion],
    );

    const handleQuestionDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
          return;
        }

        const oldIndex = category.questions.findIndex(
          (question) => question.id === active.id,
        );

        const newIndex = category.questions.findIndex(
          (question) => question.id === over.id,
        );

        if (oldIndex === -1 || newIndex === -1) {
          return;
        }

        const reorderedQuestions = arrayMove(
          category.questions,
          oldIndex,
          newIndex,
        );

        const updatedQuestions = reorderedQuestions.map((question, index) => ({
          ...question,
          order: index + 1,
        }));

        onReorderQuestions(category.id, updatedQuestions);
      },
      [category.id, category.questions, onReorderQuestions],
    );

    const adminFields = useMemo(() => {
      if (!hasPermission(Features.QA_INSIGHTS) && !hasRole(UserRole.ADMIN)) {
        return null;
      }

      return (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={category.extraTable || false}
                onChange={(e) =>
                  debouncedUpdateExtraTable(
                    category.id,
                    e.target.checked,
                    category.extraTableProperties,
                  )
                }
                sx={{
                  color: color.accent,
                  "&.Mui-checked": {
                    color: color.accent,
                  },
                }}
              />
            }
            label={t("createQuestionList.extraTable")}
            componentsProps={{
              typography: {
                sx: {
                  color: workspace.primary_dark,
                  fontSize: "0.825rem",
                  fontWeight: 400,
                },
              },
            }}
          />

          {category.extraTable && (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: workspace.primary_dark,
                  mb: 1,
                }}
              >
                {t("createQuestionList.extraTableProperties")}
              </Typography>

              <Grid container spacing={1}>
                {localExtraTableProps.map((prop, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <ComoInput
                        value={prop}
                        onChange={(e) =>
                          handleExtraTablePropChange(index, e.target.value)
                        }
                        size="small"
                        fullWidth
                        placeholder={`${t(
                          "createQuestionList.extraTableProperty",
                        )} ${index + 1}`}
                      />

                      <Tooltip title={t("createQuestionList.removeColumn")}>
                        <IconButton
                          onClick={() => removeExtraTableProperty(index)}
                          sx={{
                            color: color.red,
                          }}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <ComoButton onClick={addExtraTableProperty}>
                    {t("createQuestionList.addColumn")}
                  </ComoButton>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: workspace.primary_dark,
                mb: 1,
              }}
            >
              {t("createQuestionList.categorySubtitle")}
            </Typography>

            <ComoInput
              value={localSubtitle}
              onChange={handleSubtitleChange}
              fullWidth
              placeholder={t("createQuestionList.enterCategorySubtitle")}
            />

            <Typography
              variant="subtitle2"
              sx={{
                color: workspace.primary_dark,
                mt: 1.5,
                mb: 1,
              }}
            >
              {t("createQuestionList.categoryDescription")}
            </Typography>

            <ComoInput
              value={localDescription}
              onChange={handleDescriptionChange}
              fullWidth
              multiline
              rows={3}
              placeholder={t("createQuestionList.enterCategoryDescription")}
            />
          </Box>
        </>
      );
    }, [
      category.extraTable,
      category.id,
      category.extraTableProperties,
      t,
      localExtraTableProps,
      addExtraTableProperty,
      localSubtitle,
      handleSubtitleChange,
      workspace.primary_dark,
      localDescription,
      handleDescriptionChange,
      debouncedUpdateExtraTable,
      handleExtraTablePropChange,
      removeExtraTableProperty,
    ]);

    return (
      <Card
        sx={{
          backgroundColor: color.gray50,
          borderLeft: `4px solid ${
            isDirty ? color.orange500 : workspace.primary_dark
          }`,
          marginBottom: 2,
          transition: "border-color 0.2s ease",
          overflow: "visible",
        }}
      >
        <CardContent
          sx={{
            p: 3,
            gap: 1,
            display: "flex",
            flexDirection: "column",
            "&:last-child": {
              paddingBottom: 3,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: workspace.primary_dark,
                  mb: 1,
                }}
              >
                {t("createQuestionList.categoryName")}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <ComoInput
                  value={localName}
                  onChange={handleNameChange}
                  fullWidth
                />

                <Tooltip title={t("createQuestionList.removeCategory")}>
                  <IconButton
                    onClick={() => onRemoveCategory(category.id)}
                    sx={{
                      ml: 1,
                      color: color.red,
                    }}
                    aria-label={t("common.delete")}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={
                    expanded
                      ? t("createQuestionList.collapseCategory")
                      : t("createQuestionList.expandCategory")
                  }
                >
                  <IconButton
                    onClick={toggleCategory}
                    sx={{
                      color: color.accent,
                      ml: 1,
                    }}
                  >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Collapse in={expanded}>
            <Box
              sx={{
                gap: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {adminFields}

              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleQuestionDragEnd}
              >
                <SortableContext
                  items={category.questions.map((question) => question.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {category.questions.map((question) => (
                    <SortableQuestionItem
                      key={`${category.id}-${question.id}`}
                      question={question}
                      isDirty={
                        action === "edit" &&
                        (dirtyQuestionIds?.has(question.id) || false)
                      }
                      onRemove={() =>
                        onRemoveQuestion(category.id, question.id)
                      }
                      onEdit={(newText) =>
                        onUpdateQuestion(category.id, question.id, newText)
                      }
                      onAltTextEdit={(altText) =>
                        onUpdateQuestionAltText(
                          category.id,
                          question.id,
                          altText,
                        )
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>

              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: workspace.primary_dark,
                  }}
                >
                  {t("createQuestionList.addQuestion")}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ComoInput
                    value={localNewQuestion}
                    onChange={handleNewQuestionChange}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    placeholder={t("createQuestionList.enterQuestionText")}
                  />

                  <ComoButton onClick={handleAddQuestion}>
                    {t("createQuestionList.addQuestion")}
                  </ComoButton>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  },
);

CategoryItem.displayName = "CategoryItem";

export default CategoryItem;
